// Last-known-good copies of the data the shop needs to keep working when the
// server cannot be reached.
//
// The outbox handles writes; this handles reads. Without it an offline
// shopkeeper gets a correct error message and an empty screen, which is not
// much better than a broken one -- you cannot sell from a product list you
// cannot see.
//
// Kept in localStorage next to the outbox rather than relying on the Service
// Worker's response cache: we want the parsed records, so we can apply pending
// writes on top of them, and we want to know how old they are.

const PREFIX = "pasal_snapshot_";

export const KEYS = {
  products: "products",
  categories: "categories",
  locations: "locations",
  suppliers: "suppliers",
  // Reference lists the product form cannot be used without: with no units to
  // choose from there is nothing to save.
  units: "units",
  sizeUnits: "size_units",
  // Carries the markup and the minimum margin, so the form can fill in a tag
  // price and the till can draw a floor while offline.
  shop: "shop",
  // Dashboard aggregates. These are money figures rather than lists, so a
  // stale copy must always be shown with the time it was captured -- a
  // shopkeeper reading yesterday's takings as today's is worse than a page
  // that admits it cannot load.
  dashboardStats: "dashboard_stats",
  todaySales: "today_sales",
  lowStock: "low_stock",
};

export function saveSnapshot(key, data) {
  try {
    localStorage.setItem(
      PREFIX + key,
      JSON.stringify({ savedAt: new Date().toISOString(), data }),
    );
  } catch (err) {
    // Snapshots are an optimisation, never the source of truth for a write.
    // Losing one is survivable, so warn rather than throw into the caller.
    console.warn(`snapshot: could not save ${key}`, err);
  }
}

export function loadSnapshot(key) {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Aggregates are objects, lists are arrays -- accept either, reject only
    // a malformed envelope.
    return parsed && "data" in parsed ? parsed : null;
  } catch {
    return null;
  }
}

export function clearSnapshots() {
  Object.values(KEYS).forEach((k) => localStorage.removeItem(PREFIX + k));
}

/**
 * Fetch through the snapshot: try the network, fall back to the last copy.
 *
 * Returns {data, stale, savedAt}. `stale` is true when the data came from the
 * snapshot, so callers can say so instead of silently showing old numbers.
 * Re-throws only when there is no snapshot to fall back on -- at that point
 * the page genuinely has nothing to render and should show its error state.
 */
export async function fetchWithSnapshot(key, fetcher) {
  try {
    const data = await fetcher();
    saveSnapshot(key, data);
    return { data, stale: false, savedAt: null };
  } catch (err) {
    const snap = loadSnapshot(key);
    if (!snap) throw err;
    return { data: snap.data, stale: true, savedAt: snap.savedAt };
  }
}

// Matches "/products/<id>" and "/products/<id>/stock/adjust", where <id> is a
// UUID. Deliberately strict: with a loose [^/]+ a future collection route like
// "/products/stock/bulk" would parse as a product whose id was "stock" and get
// silently applied to nothing.
const PRODUCT_PATH =
  /^\/products\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(\/stock\/adjust)?$/i;

// Quantities are floats. Three decimals is past anything a shop weighs to, and
// it stops 0.1 + 0.2 reaching the screen as 0.30000000000000004.
const round3 = (n) => Math.round(n * 1000) / 1000;

/**
 * Replay the outbox on top of a product list so the screen shows what the
 * shopkeeper has actually done, not just what has reached the server.
 *
 * Everything queued here has already happened in the shop: the goods left the
 * shelf, the new item is on it, the price was changed. Showing the server's
 * older view would mean selling the same last item twice, or hunting for a
 * product that was added ten minutes ago.
 *
 * Entries are applied in queue order, which is the order they were made.
 */
export function applyPendingWrites(products, queue) {
  if (!queue?.length) return products;

  let list = products.slice();

  const patch = (id, fn) => {
    list = list.map((p) => (p.id === id ? fn(p) : p));
  };

  for (const entry of queue) {
    const { method, path, body } = entry;

    if (path === "/sales") {
      for (const item of body?.items || []) {
        // A sale line's quantity is in PIECES whatever basis it was priced on,
        // so this is the same subtraction for a piece, a dozen or 16 kg of
        // thali. Rounded because quantities are floats now and 0.1 + 0.2 must
        // not reach the screen.
        patch(item.product_id, (p) => ({
          ...p,
          quantity: round3(Math.max(0, (p.quantity || 0) - (item.quantity || 0))),
        }));
      }
      continue;
    }

    if (path === "/products" && method === "post") {
      // A product created offline has no server record yet, so it exists only
      // here until the queue drains.
      // Every field the product list and the product form read has to have a
      // value here. This list is the server's defaults duplicated, which is
      // unlovely, but the alternative is a product added offline rendering with
      // blank prices and sizes -- and ProductForm reading it back out and
      // throwing on an undefined it expected to be a number.
      list.push({
        name_np: "",
        brand: "",
        location: "shelf_top",
        size_value: null,
        size_unit: null,
        gauge: null,
        variant_group: null,
        unit: "pcs",
        purchase_unit: null,
        pack_size: null,
        weight_g: null,
        cost_price: 0,
        mrp: 0,
        min_price: null,
        recent_prices: [],
        quantity: 0,
        quantity_type: "exact",
        low_stock_threshold: 5,
        is_active: true,
        ...body,
        updated_at: entry.queuedAt,
        _pending: true,
      });
      continue;
    }

    const match = PRODUCT_PATH.exec(path || "");
    if (!match) continue;
    const [, id, isAdjust] = match;

    if (isAdjust) {
      patch(id, (p) => ({
        ...p,
        quantity: round3(Math.max(0, (p.quantity || 0) + (body?.delta || 0))),
      }));
    } else if (method === "put") {
      patch(id, (p) => ({ ...p, ...body, updated_at: entry.queuedAt, _pending: true }));
    } else if (method === "delete") {
      list = list.filter((p) => p.id !== id);
    }
  }

  return list;
}

// Kept so existing call sites keep working; sales are one case of the above.
export const applyPendingSales = applyPendingWrites;

/**
 * The same idea for a plain collection: suppliers, categories, locations.
 *
 * `basePath` is the collection route, e.g. "/suppliers". Queued creates are
 * appended, edits merged, deletes removed -- so a supplier added with no
 * connection appears in the list it was added from, rather than seeming not to
 * have saved.
 */
export function applyPendingList(items, queue, basePath) {
  if (!queue?.length) return items;

  let list = items.slice();
  const itemPath = new RegExp(`^${basePath}/([^/]+)$`);

  for (const entry of queue) {
    const { method, path, body } = entry;

    if (path === basePath && method === "post") {
      list.push({ is_active: true, ...body, _pending: true });
      continue;
    }

    const match = itemPath.exec(path || "");
    if (!match) continue;
    const id = match[1];

    if (method === "put") {
      list = list.map((x) => (x.id === id ? { ...x, ...body, _pending: true } : x));
    } else if (method === "delete") {
      list = list.filter((x) => x.id !== id);
    }
  }

  return list;
}
